import { defineMessages } from 'react-intl';

import { pluralTerm } from '../../lib/helpers/term.helper';
import { SportTermLookup, SportTerminology } from '../../lib/types';

export const scope = 'app.components.ReportCardAccess';

export const getMessages = (termLookup: SportTermLookup) => {
  const athleteTerm = termLookup[SportTerminology.ATHLETE].toLowerCase();
  const athletesTerm = pluralTerm(athleteTerm);
  return defineMessages({
    lblIncludeNonConcludedDefault: {
      id: `${scope}.lblIncludeNonConcludedDefault`,
      defaultMessage: `Include ${athletesTerm} who haven't concluded when sending report cards`,
    },
    captionIncludeNonConcludedAssociationDefault: {
      id: `${scope}.captionIncludeNonConcludedAssociationDefault`,
      defaultMessage: `When off, report cards are sent only to concluded ${athletesTerm}. An assessment or age group can choose differently.`,
    },
    captionIncludeNonConcludedAssessmentDefault: {
      id: `${scope}.captionIncludeNonConcludedAssessmentDefault`,
      defaultMessage: `Applies to report card sends in this assessment. While left unset (—), the association-level default applies instead. An age group can still choose differently.`,
    },

    statusNoData: { id: `${scope}.statusNoData`, defaultMessage: 'No report card' },
    statusNotReleased: { id: `${scope}.statusNotReleased`, defaultMessage: 'Not sent' },
    statusAvailable: { id: `${scope}.statusAvailable`, defaultMessage: 'Available' },
    statusRevoked: { id: `${scope}.statusRevoked`, defaultMessage: 'Revoked' },
    statusAgeGroupRevoked: { id: `${scope}.statusAgeGroupRevoked`, defaultMessage: 'Age group revoked' },

    lblReportCard: { id: `${scope}.lblReportCard`, defaultMessage: 'Report card' },
    msgSentOn: { id: `${scope}.msgSentOn`, defaultMessage: 'Sent {date}' },
    msgRevokedOn: { id: `${scope}.msgRevokedOn`, defaultMessage: 'Access revoked {date}' },
    captionNoData: { id: `${scope}.captionNoData`, defaultMessage: `This ${athleteTerm} doesn't have a report card yet.` },
    captionNotReleased: {
      id: `${scope}.captionNotReleased`,
      defaultMessage: "Their contacts can't see it yet. Report cards are sent from the age group's reports page or, one at a time, from the report card actions.",
    },
    captionNotReleasedReadOnly: {
      id: `${scope}.captionNotReleasedReadOnly`,
      defaultMessage: "Their contacts can't see it yet. Report cards are sent from the age group's reports page.",
    },
    captionNotReleasedAssociationOff: {
      id: `${scope}.captionNotReleasedAssociationOff`,
      defaultMessage: "Their contacts can't see it until it's sent from the report card actions.",
    },
    captionAssociationOff: {
      id: `${scope}.captionAssociationOff`,
      defaultMessage: `Report cards are turned off for your association. This ${athleteTerm}'s report card can still be sent from here, and links sent to their contacts open it.`,
    },
    captionRevoked: {
      id: `${scope}.captionRevoked`,
      defaultMessage: "Their contacts can't open the report card, and report card sends skip them, until access is re-enabled.",
    },
    captionAgeGroupRevoked: {
      id: `${scope}.captionAgeGroupRevoked`,
      defaultMessage: "Access is turned off for the whole age group. Re-enable it from the age group's reports page.",
    },
    errStatusNotLoaded: { id: `${scope}.errStatusNotLoaded`, defaultMessage: "The report card status couldn't be loaded. Please refresh the page." },
    errActionsNotLoaded: { id: `${scope}.errActionsNotLoaded`, defaultMessage: "This player's report card actions couldn't be loaded. Please try again." },

    lblActions: { id: `${scope}.lblActions`, defaultMessage: 'Report card actions' },
    actionView: { id: `${scope}.actionView`, defaultMessage: 'View report card' },
    actionSend: { id: `${scope}.actionSend`, defaultMessage: 'Send report card' },
    actionResend: { id: `${scope}.actionResend`, defaultMessage: 'Resend report card' },
    captionThisPlayerOnly: { id: `${scope}.captionThisPlayerOnly`, defaultMessage: `To this ${athleteTerm}'s contacts only` },
    actionCopyLink: { id: `${scope}.actionCopyLink`, defaultMessage: 'Copy contact link' },
    captionCopyLink: { id: `${scope}.captionCopyLink`, defaultMessage: 'Choose a contact' },
    lblSelectContact: { id: `${scope}.lblSelectContact`, defaultMessage: 'Select contact' },
    actionExtendLinks: { id: `${scope}.actionExtendLinks`, defaultMessage: 'Extend link expiry' },
    captionExtendLinks: { id: `${scope}.captionExtendLinks`, defaultMessage: 'Links will expire a year from today' },
    actionRevoke: { id: `${scope}.actionRevoke`, defaultMessage: 'Revoke access' },
    captionRevoke: { id: `${scope}.captionRevoke`, defaultMessage: 'Links stop opening it, and sends skip them' },
    actionReEnable: { id: `${scope}.actionReEnable`, defaultMessage: 'Re-enable access' },
    captionReEnable: { id: `${scope}.captionReEnable`, defaultMessage: 'Sent links open it again, and sends include them' },

    btnCancel: { id: `${scope}.btnCancel`, defaultMessage: 'Cancel' },
    confirmSendTitle: { id: `${scope}.confirmSendTitle`, defaultMessage: "Send {name}'s report card?" },
    confirmSendBody: {
      id: `${scope}.confirmSendBody`,
      defaultMessage: `{name}'s contacts are emailed or texted a link, and can open the report card from then on. No other ${athletesTerm} are sent anything.`,
    },
    confirmSendButton: { id: `${scope}.confirmSendButton`, defaultMessage: 'Send' },
    confirmResendTitle: { id: `${scope}.confirmResendTitle`, defaultMessage: "Resend {name}'s report card?" },
    confirmResendBody: {
      id: `${scope}.confirmResendBody`,
      defaultMessage: `{name}'s contacts are emailed or texted the report card link again. No other ${athletesTerm} are sent anything.`,
    },
    confirmResendButton: { id: `${scope}.confirmResendButton`, defaultMessage: 'Resend' },
    confirmRevokeTitle: { id: `${scope}.confirmRevokeTitle`, defaultMessage: 'Revoke report card access for {name}?' },
    confirmRevokeBody: {
      id: `${scope}.confirmRevokeBody`,
      defaultMessage: "{name}'s contacts won't be able to open the report card, and report card sends will skip them. Nobody is notified, and you can re-enable access later.",
    },
    confirmRevokeButton: { id: `${scope}.confirmRevokeButton`, defaultMessage: 'Revoke access' },
    confirmReEnableTitle: { id: `${scope}.confirmReEnableTitle`, defaultMessage: 'Re-enable report card access for {name}?' },
    confirmReEnableBody: {
      id: `${scope}.confirmReEnableBody`,
      defaultMessage: "If {name}'s report card was sent, their links open it again unless the whole age group is turned off; if not, report card sends stop skipping them. Nobody is notified.",
    },
    confirmReEnableButton: { id: `${scope}.confirmReEnableButton`, defaultMessage: 'Re-enable access' },

    msgSent: {
      id: `${scope}.msgSent`,
      defaultMessage: "{name}'s report card was sent to {count} {contacts} ({emailCount} {emails}, {smsCount} {texts}).",
    },
    msgSentWithUnreachable: {
      id: `${scope}.msgSentWithUnreachable`,
      defaultMessage: "{name}'s report card was sent to {count} {contacts} ({emailCount} {emails}, {smsCount} {texts}). {unreachableCount} other {unreachableContacts} can't be emailed or texted, so {were} not told.",
    },
    msgSentNobodyNotified: {
      id: `${scope}.msgSentNobodyNotified`,
      defaultMessage: "{name}'s report card is now available, but none of their contacts can be emailed or texted, so nobody was told.",
    },
    msgNotSentNotConcluded: {
      id: `${scope}.msgNotSentNotConcluded`,
      defaultMessage: `{name}'s report card wasn't sent: they haven't concluded, and this age group sends report cards only to concluded ${athletesTerm}.`,
    },
    msgAlreadySent: {
      id: `${scope}.msgAlreadySent`,
      defaultMessage: "{name}'s report card is available to their contacts. It had already been sent, so no new message went out.",
    },
    msgNothingSent: {
      id: `${scope}.msgNothingSent`,
      defaultMessage: "Nothing was sent: {name}'s report card can't be sent right now. Refresh to see the current status.",
    },
    msgSendIncomplete: {
      id: `${scope}.msgSendIncomplete`,
      defaultMessage: "{name}'s report card was sent, but not to every contact. The ones that failed are retried automatically; their Emails & Text Messages list shows each attempt.",
    },
    msgResent: {
      id: `${scope}.msgResent`,
      defaultMessage: "{name}'s report card was resent to {count} {contacts} ({emailCount} {emails}, {smsCount} {texts}).",
    },
    msgResentWithUnreachable: {
      id: `${scope}.msgResentWithUnreachable`,
      defaultMessage: "{name}'s report card was resent to {count} {contacts} ({emailCount} {emails}, {smsCount} {texts}). {unreachableCount} other {unreachableContacts} can't be emailed or texted, so {were} not messaged. Every contact's link was renewed.",
    },
    msgResentLinksRenewed: {
      id: `${scope}.msgResentLinksRenewed`,
      defaultMessage: "Nobody was messaged: none of {name}'s contacts can be emailed or texted. Their report card links were renewed.",
    },
    msgNothingResentNoContacts: {
      id: `${scope}.msgNothingResentNoContacts`,
      defaultMessage: "Nothing was resent: none of {name}'s contacts can be emailed, texted or sent a new link.",
    },
    msgNothingResent: {
      id: `${scope}.msgNothingResent`,
      defaultMessage: "Nothing was resent: {name} can't open their report card right now. Their access may have just been revoked; refresh to see the current status.",
    },
    msgRevoked: { id: `${scope}.msgRevoked`, defaultMessage: 'Report card access revoked for {name}.' },
    msgReEnabled: { id: `${scope}.msgReEnabled`, defaultMessage: 'Report card access re-enabled for {name}.' },
    msgReEnabledNotSent: {
      id: `${scope}.msgReEnabledNotSent`,
      defaultMessage: "Report card sends no longer skip {name}. Their report card hasn't been sent yet.",
    },
    msgLinksExtended: { id: `${scope}.msgLinksExtended`, defaultMessage: "{name}'s report card links now expire a year from today." },
    msgNoLinksToExtend: {
      id: `${scope}.msgNoLinksToExtend`,
      defaultMessage: "{name} has no current report card links to extend. Resend the report card to send new ones.",
    },
    msgLinkCopied: { id: `${scope}.msgLinkCopied`, defaultMessage: 'Report card link copied for {contact}.' },

    errSendFailed: { id: `${scope}.errSendFailed`, defaultMessage: "{name}'s report card wasn't sent. Please try again." },
    errSendRefused: { id: `${scope}.errSendRefused`, defaultMessage: "{name}'s report card wasn't sent: {reason}" },
    errResendFailed: { id: `${scope}.errResendFailed`, defaultMessage: "{name}'s report card wasn't resent. Please try again." },
    errResendRefused: { id: `${scope}.errResendRefused`, defaultMessage: "{name}'s report card wasn't resent: {reason}" },
    errResendSending: {
      id: `${scope}.errResendSending`,
      defaultMessage: "{name}'s report card wasn't resent: the last one is still being delivered. Wait a few minutes, then resend it if it hasn't arrived.",
    },
    errResendRetrying: {
      id: `${scope}.errResendRetrying`,
      defaultMessage: "{name}'s report card wasn't resent: the last one didn't reach every contact and is being retried automatically every few minutes. Resend it once the retries finish if it still hasn't arrived.",
    },
    msgResendIncomplete: {
      id: `${scope}.msgResendIncomplete`,
      defaultMessage: "{name}'s report card was resent, but not to every contact. The ones that failed are retried automatically; their Emails & Text Messages list shows each attempt.",
    },
    errRevokeFailed: { id: `${scope}.errRevokeFailed`, defaultMessage: "Report card access for {name} wasn't revoked. Please try again." },
    errRevokeRefused: { id: `${scope}.errRevokeRefused`, defaultMessage: "Report card access for {name} wasn't revoked: {reason}" },
    errReEnableFailed: { id: `${scope}.errReEnableFailed`, defaultMessage: "Report card access for {name} wasn't re-enabled. Please try again." },
    errReEnableRefused: { id: `${scope}.errReEnableRefused`, defaultMessage: "Report card access for {name} wasn't re-enabled: {reason}" },
    errExtendLinksFailed: { id: `${scope}.errExtendLinksFailed`, defaultMessage: "{name}'s report card links weren't extended. Please try again." },
    errExtendLinksRefused: { id: `${scope}.errExtendLinksRefused`, defaultMessage: "{name}'s report card links weren't extended: {reason}" },
    errLinkNotCopied: { id: `${scope}.errLinkNotCopied`, defaultMessage: "The link wasn't copied to your clipboard. Please try again." },
  });
};
